import { useState } from 'react';
import { VStack, Text } from 'native-base';

// Components
import {
  PrimaryButton,
  ModalWrapper,
  TextField,
  LocalComponentError,
} from '../../';

// Redux
import {
  closeModal,
  useAppDispatch,
  openModal,
  useGetCurrentUserQuery,
} from '@breakroom-nx/redux';

import * as Yup from 'yup';

import { Formik, ErrorMessage } from 'formik';
import { httpsCallable } from 'firebase/functions';

// Assets
import { theme } from '@breakroom-nx/assets';
import { functions } from '@breakroom-nx/apis';

import { InputType, AccountType, Status } from '@breakroom-nx/helpers';

const InviteSchema = Yup.object().shape({
  email: Yup.string()
    .email('Invalid email')
    .required("Enter your friend's email"),
});

export const InviteFriendToApp = () => {
  const appDispatch = useAppDispatch();
  const [textError, setTextError] = useState<string | undefined>(undefined);
  const [sending, setSending] = useState<boolean>(false);

  const { data: currentUser } = useGetCurrentUserQuery(null);
  const sendInvite = httpsCallable(functions, 'user-invitefriendtoapp');

  async function inviteFriendToApp(recipientEmail: string) {
    if (currentUser) {
      await sendInvite({
        username: currentUser.username,
        name: currentUser.name,
        email: recipientEmail,
      })
        .then(() => {
          appDispatch(closeModal());
          appDispatch(
            openModal({
              modalType: 'Alert',
              modalProps: {
                status: Status.success,
                title: `Invite successfully sent!`,
              },
            })
          );
        })
        .catch((err) => {
          setTextError(`Error: ${err}`);
        });
    } else {
      setTextError(
        `There was an issue authenticating your logged in state, please try again.`
      );
    }
  }

  return (
    <ModalWrapper>
      <VStack
        bg={'surface.100'}
        space={3}
        textAlign={'center'}
        style={{
          borderColor: theme.colors.surface[200],
          borderWidth: 1,
          borderRadius: 25,
          shadowColor: theme.colors.surface[100],
          shadowOpacity: 0.25,
          shadowRadius: 20,
          shadowOffset: {
            width: 10,
            height: 20,
          },
          padding: 30,
        }}
      >
        <VStack space={4}>
          <LocalComponentError error={textError} />
          <VStack space={1}>
            <VStack space={1}>
              <Text
                fontFamily={'heading'}
                fontSize={23}
                color={'yellow.300'}
                fontWeight={600}
                mb={'3px'}
              >
                The More the Merrier
              </Text>
              <Text
                fontFamily={'heading'}
                fontSize={16}
                color={'yellow.500'}
                fontWeight={600}
                mb={'3px'}
              >
                Get gems when you refer your friends!
              </Text>
            </VStack>
          </VStack>
          <Formik
            initialValues={{
              email: '',
            }}
            validationSchema={InviteSchema}
            onSubmit={async (values, { setSubmitting }) => {
              setSending(true);
              inviteFriendToApp(values.email);
            }}
          >
            {({
              validateOnBlur = true,
              handleChange,
              handleBlur,
              handleSubmit,
              values,
            }) => (
              <>
                <VStack space={'3'} width={'full'}>
                  <TextField
                    type={InputType.Email}
                    onChangeText={handleChange('email')}
                    onBlur={handleBlur('email')}
                    value={values.email}
                    color={'yellow.300'}
                    autoFocus
                  />
                  <Text variant={'error'}>
                    <ErrorMessage name="email" />
                  </Text>
                </VStack>
                <VStack mt={5} space={4} width={'full'} alignItems={'center'}>
                  <PrimaryButton
                    disabled={values.email === '' || sending || !currentUser}
                    text={'Invite'}
                    bg={'yellow.300'}
                    color={'surface.100'}
                    isLoading={sending}
                    onPress={handleSubmit}
                    width={'100%'}
                  />
                </VStack>
              </>
            )}
          </Formik>
        </VStack>
      </VStack>
    </ModalWrapper>
  );
};
